import React from "react";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import Checkbox from "@amzn/meridian/checkbox";
import Link from "@amzn/meridian/link";
import Tag from "@amzn/meridian/tag";
import { DataTableCell, DataTableRow } from "@amzn/meridian/data-table";
import { AuditResult } from "src/store/auditSlice";
import { formatTimestampToDate } from "src/utils/dateUtil";
import { Incident, IncidentField } from "./types";
import { getDefectType, getDefectTypeSummary, isFieldRelevant } from "./utils";
import { CORE_FIELDS, COLORS } from "./constants";

interface AuditTableRowProps {
  incident: Incident;
  index: number;
  selectedRows: string[];
  onToggleRowSelection: (incidentId: string) => void;
  showAllFields: boolean;
  includeAllFields: boolean;
  tabType?: "all" | "human" | "ai";
  expandAllRows?: boolean;
  selectedBusinessUnits?: string[];
  selectedOrganizations?: string[];
  selectedScopes?: string[];
  selectedSiteTypes?: string[];
  selectedSites?: string[];
}

const AuditTableRow = ({
  incident,
  index,
  selectedRows,
  onToggleRowSelection,
  showAllFields,
  includeAllFields = false,
  tabType = "all",
  expandAllRows = false,
  selectedBusinessUnits = [],
  selectedOrganizations = [],
  selectedScopes = [],
  selectedSiteTypes = [],
  selectedSites = [],
}: AuditTableRowProps) => {
  // Filter fields based on tab type
  const getFilteredFields = (fields: IncidentField[]) => {
    switch (tabType) {
      case "human":
        return fields.filter(
          (field) =>
            field.auditResult === AuditResult.EHS_SMART_ASSISTANT_CORRECT ||
            field.auditResult === AuditResult.LLM_CORRECT,
        );
      case "ai":
        return fields.filter(
          (field) =>
            field.auditResult === AuditResult.HUMAN_CORRECT ||
            field.auditResult === AuditResult.NEITHER,
        );
      default:
        return fields.filter(
          (field) =>
            // Include fields based on includeAllFields toggle
            isFieldRelevant(field.fieldName, includeAllFields) &&
            field.auditResult !== AuditResult.BOTH_CORRECT &&
            field.auditResult !== AuditResult.BOTH_AUSTIN_AND_EHS_SMART_ASSISTANT_CORRECT &&
            (field.auditResult === AuditResult.NEITHER ||
              field.auditResult === AuditResult.EHS_SMART_ASSISTANT_CORRECT ||
              field.auditResult === AuditResult.LLM_CORRECT ||
              field.auditResult === AuditResult.HUMAN_CORRECT),
        );
    }
  };

  const fieldsWithIssues = getFilteredFields(incident.fields);
  const childrenIds =
    fieldsWithIssues.length > 0
      ? fieldsWithIssues.map((field, fieldIndex) => `${incident.incidentId}-field-${fieldIndex}`)
      : [];

  const handleAuditClick = () => {
    if (incident.auditWorkflowId) {
      const auditUrl = `/audit/${incident.auditWorkflowId}/${incident.incidentId}`;
      window.open(auditUrl, "_blank");
    } else {
      console.log(`No audit workflow ID available for incident ${incident.incidentId}`);
    }
  };

  const getDefectTypeLabel = () => {
    switch (tabType) {
      case "human":
        return "Human";
      case "ai":
        return "AI";
      default:
        return getDefectTypeSummary(fieldsWithIssues);
    }
  };

  return [
    <DataTableRow
      key={incident.incidentId}
      rowId={incident.incidentId}
      childrenIds={childrenIds}
      isExpanded={expandAllRows}
      highlightOnHover={true}
      data-cy={`${tabType}-incident-row-${index}__row`}
    >
      <DataTableCell>
        <Checkbox
          checked={selectedRows.includes(incident.incidentId)}
          onChange={() => onToggleRowSelection(incident.incidentId)}
        />
      </DataTableCell>
      <DataTableCell hasExpander={true}>
        <Column>
          <Link type="secondary" onClick={handleAuditClick}>
            {incident.caseId}
          </Link>
          <Text type="b100" color="secondary">
            {formatTimestampToDate(incident.dateReported)}
          </Text>
        </Column>
      </DataTableCell>
      <DataTableCell>
        <Column alignmentHorizontal="start">
          <div style={{ display: "inline-block", width: "fit-content" }}>
            {fieldsWithIssues.length > 0 ? (
              <Tag type="error">
                {`${fieldsWithIssues.length} Defect${fieldsWithIssues.length > 1 ? "s" : ""}`}
              </Tag>
            ) : (
              <Tag type="success">No Defects</Tag>
            )}
          </div>
          <Text type="b100" color="secondary">
            {getDefectTypeLabel()}
          </Text>
        </Column>
      </DataTableCell>
      <DataTableCell>
        <Text type="b100">{incident.dqoCount}</Text>
      </DataTableCell>
      <DataTableCell>
        <Text type="b100">{incident.aiDefectCount}</Text>
      </DataTableCell>
      <DataTableCell>
        <Text type="b100">{incident.aiSource}</Text>
      </DataTableCell>
      <DataTableCell>{incident.reviewer}</DataTableCell>
      {selectedSiteTypes.length > 0 && <DataTableCell>{incident.siteType}</DataTableCell>}
      {selectedSites.length > 0 && <DataTableCell>{incident.site}</DataTableCell>}
      {selectedBusinessUnits.length > 0 && <DataTableCell>{incident.obr_bu}</DataTableCell>}
      {selectedOrganizations.length > 0 && <DataTableCell>{incident.obr_org}</DataTableCell>}
      {selectedScopes.length > 0 && <DataTableCell>{incident.obr_scope}</DataTableCell>}
    </DataTableRow>,

    // Child rows - always show only fields with defects
    ...fieldsWithIssues.map((field: IncidentField, fieldIndex: number) => (
      <DataTableRow
        key={`${incident.incidentId}-field-${fieldIndex}`}
        rowId={`${incident.incidentId}-field-${fieldIndex}`}
        parentId={incident.incidentId}
        depth={2}
        highlightOnHover={true}
      >
        <DataTableCell>{""}</DataTableCell>
        <DataTableCell>
          <Text type="b100">{field.fieldName}</Text>
        </DataTableCell>
        <DataTableCell>
          <Tag
            type={
              field.auditResult === AuditResult.BOTH_CORRECT ||
              field.auditResult === AuditResult.BOTH_AUSTIN_AND_EHS_SMART_ASSISTANT_CORRECT
                ? "success"
                : field.auditResult === AuditResult.NEITHER
                  ? "error"
                  : "warning"
            }
          >
            {getDefectType(field.auditResult)}
          </Tag>
        </DataTableCell>
        <DataTableCell>
          <Text type="b100">
            <strong>AUSTIN:</strong>{" "}
            <span
              style={{
                color: field.austinAnswer === field.confirmedAnswer ? COLORS.BLUE : COLORS.RED,
              }}
            >
              {field.austinAnswer?.replace(/\|/g, ", ")}
            </span>
          </Text>
        </DataTableCell>
        <DataTableCell>
          <Text type="b100">
            <strong>AI:</strong>{" "}
            <span
              style={{
                color: field.llmAnswer === field.confirmedAnswer ? COLORS.BLUE : COLORS.RED,
              }}
            >
              {field.llmAnswer?.replace(/\|/g, ", ")}
            </span>
          </Text>
        </DataTableCell>
        <DataTableCell>
          <Text type="b100">
            <strong>Audited:</strong>{" "}
            <span style={{ color: COLORS.BLUE }}>
              {field.confirmedAnswer?.replace(/\|/g, ", ")}
            </span>
          </Text>
        </DataTableCell>
        <DataTableCell>{""}</DataTableCell>
        {selectedSiteTypes.length > 0 && <DataTableCell>{""}</DataTableCell>}
        {selectedSites.length > 0 && <DataTableCell>{""}</DataTableCell>}
        {selectedBusinessUnits.length > 0 && <DataTableCell>{""}</DataTableCell>}
        {selectedOrganizations.length > 0 && <DataTableCell>{""}</DataTableCell>}
        {selectedScopes.length > 0 && <DataTableCell>{""}</DataTableCell>}
      </DataTableRow>
    )),
  ];
};

export default AuditTableRow;
